import React, { useMemo, useState } from 'react';
import { AddCircleLiner24 } from "../../icons";
import { Autocomplete } from "../Autocomplete";
import { getOptionLabel, getOptionSelected } from "../Autocomplete/AutocompleteSelectors";
import { Column } from "../Layout/Column";
import { FullRow } from "../Layout/FullRow";
import { MenuPromotionalAction } from "../Menu/MenuPromotionalAction/MenuPromotionalAction";
import { TextButton } from "../TextButton";
import { Tooltip } from "../Tooltip";
import { Typography } from "../Typography";
import styles from "./EnhancedAutocomplete.module.scss";
import { EnhancedAutocompleteTestIds, EnhancedAutocompleteOptionType, getOptions, mapDataToOption } from "./EnhancedAutocomplete.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const EnhancedAutocomplete = props => {
  var _value$label, _newItemOption$icon;
  const {
    ['data-testid']: testId,
    applyOptionChange: _applyOptionChange,
    options: _options,
    menuWidth,
    emptySearchText,
    newItemOption,
    promotionalActionOption,
    isLoading,
    onRefresh,
    textFieldProps,
    className,
    style,
    selectedOption,
    disabled,
    itemRenderer,
    size,
    error,
    helperText,
    disabledItems
  } = props;
  const disabledItemsMap = useMemo(() => {
    if (!disabledItems) return new Map();
    return new Map(disabledItems.map(item => [item.id, item.tooltip]));
  }, [disabledItems]);
  const [managedCurrentOption, setManagedCurrentOption] = useState(null);
  const externalCurrentOption = useMemo(() => {
    return selectedOption ? mapDataToOption(selectedOption, props.keyField, props.labelField) : null;
  }, [selectedOption, props.keyField, props.labelField]);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const currentOption = useMemo(() => {
    return managedCurrentOption !== null && managedCurrentOption !== void 0 ? managedCurrentOption : externalCurrentOption;
  }, [managedCurrentOption, externalCurrentOption]);
  const options = useMemo(() => {
    return getOptions(_options, props.keyField, props.labelField);
  }, [_options, props.keyField, props.labelField]);
  const value = currentOption;
  const valueLabel = (_value$label = value === null || value === void 0 ? void 0 : value.label) !== null && _value$label !== void 0 ? _value$label : '';
  const applyOptionChange = newOption => {
    setManagedCurrentOption(newOption);
    const item = newOption === null || newOption === void 0 ? void 0 : newOption.data;
    _applyOptionChange === null || _applyOptionChange === void 0 || _applyOptionChange(item);
  };
  return /*#__PURE__*/_jsx(Autocomplete, {
    id: testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
    error: !!error,
    helperText: helperText,
    disabled: disabled,
    selectOnFocus: true,
    className: className,
    style: style,
    size: size || 'small',
    menuWidth: menuWidth,
    textFieldProps: textFieldProps,
    forcePopupIcon: true,
    "data-testid": testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
    loading: isLoading,
    options: options,
    clearOnEscape: true,
    popoverFooter: (newItemOption || promotionalActionOption) && /*#__PURE__*/_jsxs(Column, {
      className: styles.popoverFooter,
      children: [newItemOption && /*#__PURE__*/_jsx(TextButton, {
        className: styles.addNewItemOption,
        "data-testid": EnhancedAutocompleteTestIds.NewItemOption(),
        underline: "none",
        prefixIcon: (_newItemOption$icon = newItemOption.icon) !== null && _newItemOption$icon !== void 0 ? _newItemOption$icon : /*#__PURE__*/_jsx(AddCircleLiner24, {}),
        onMouseDown: async () => {
          const newItem = await newItemOption.onClick();
          await (onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh());
          if (newItem) {
            applyOptionChange(mapDataToOption(newItem, props.keyField, props.labelField));
          }
        },
        children: newItemOption.text
      }), promotionalActionOption && /*#__PURE__*/_jsx(MenuPromotionalAction, {
        testId: EnhancedAutocompleteTestIds.PromotionalActionOption(),
        title: promotionalActionOption.title,
        body: promotionalActionOption.body,
        onMouseDown: () => {
          promotionalActionOption.onClick();
        }
      })]
    }),
    renderOption: option => {
      if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.EmptySearch) {
        return /*#__PURE__*/_jsx(Typography, {
          as: "p",
          "data-testid": EnhancedAutocompleteTestIds.EmptySearchOption(),
          children: option.label
        });
      }
      const tooltip = disabledItemsMap.get(option.value);
      const content = /*#__PURE__*/_jsx(FullRow, {
        "data-testid": EnhancedAutocompleteTestIds.EnhancedAutocompleteOption(option.value),
        children: itemRenderer ? itemRenderer(option.data) : /*#__PURE__*/_jsx(Typography, {
          as: "p",
          ellipsis: true,
          children: option.label
        })
      });
      if (tooltip) {
        return /*#__PURE__*/_jsx(Tooltip, {
          title: tooltip,
          placement: "right",
          "data-testid": EnhancedAutocompleteTestIds.EnhancedAutocompleteOptionTooltip(option.value),
          children: /*#__PURE__*/_jsx("span", {
            style: {
              display: 'block',
              width: '100%'
            },
            children: content
          })
        });
      }
      return content;
    },
    filterOptions: options => {
      const NO_MATCHES_OPTION = {
        value: 'empty-search',
        label: emptySearchText,
        type: EnhancedAutocompleteOptionType.EmptySearch
      };
      const filteredOptions = showAllOptions ? options : options.filter(option => option.label.toLowerCase().includes(valueLabel.toLowerCase()));
      return filteredOptions.length === 0 ? [NO_MATCHES_OPTION] : filteredOptions;
    },
    freeSolo: true,
    value: value,
    inputValue: valueLabel,
    getOptionLabel: getOptionLabel,
    getOptionSelected: getOptionSelected,
    onBlur: () => {
      if ((currentOption === null || currentOption === void 0 ? void 0 : currentOption.type) !== EnhancedAutocompleteOptionType.Item) {
        applyOptionChange(null);
      }
    },
    onOpen: () => {
      setShowAllOptions(true);
    },
    onInputChange: (_, value, reason) => {
      setShowAllOptions(false);
      if (reason === 'clear') {
        applyOptionChange(null);
        return;
      }
      if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
      const option = options.find(s => s.label === value);
      const fallbackServiceOptionValue = {
        value: '',
        label: value,
        type: EnhancedAutocompleteOptionType.IncompleteSearch
      };
      applyOptionChange(option !== null && option !== void 0 ? option : fallbackServiceOptionValue);
    },
    noOptionsText: emptySearchText,
    getOptionDisabled: option => option.type === EnhancedAutocompleteOptionType.EmptySearch || disabledItemsMap.has(option.value),
    onChange: async (event, value) => {
      const newValue = value ? value : null;
      applyOptionChange(newValue);
    }
  });
};